// FILE: src/pages/StudentTicketDetailPage.jsx
// One support request (plan WP-8, MTO 10.1): what the student asked, the
// conversation with student support, and a reply box. Message bubbles meet
// AA contrast in both themes (student: primary tint + body text; staff: page
// background with a border). A resolved request can't take replies; the
// page says why and links to a new request. Errors go through errorMessage.
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link as RouterLink, useLocation, useNavigate, useParams } from "react-router-dom";
import { Alert, Box, Button, Card, CardContent, CircularProgress, Link, Stack, TextField, Typography } from "@mui/material";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import SupportAgentOutlinedIcon from "@mui/icons-material/SupportAgentOutlined";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import { fetchTicketDetail, postTicketMessage } from "../features/supportSlice";
import { DetailList, ErrorState, LoadingState, PageHeader, Section } from "../components/ui";
import { CategoryChip, TicketStatusChip, formatDateTime } from "../components/support/ticketDisplay";
import { ticketCategoryLabel } from "../i18n/labels";
import { errorMessage } from "../i18n/errors";

export const REPLY_MAX = 2000;
const BREADCRUMBS = [{ label: "Support", to: "/student/support" }, { label: "Request" }];

function isoOrUndefined(value) {
  const d = value ? new Date(value) : null;
  return d && !Number.isNaN(d.getTime()) ? d.toISOString() : undefined;
}

function Bubble({ mine, text, at }) {
  const Icon = mine ? PersonOutlineIcon : SupportAgentOutlinedIcon;
  return (
    <Box component="li" sx={{ display: "flex", justifyContent: mine ? "flex-end" : "flex-start" }}>
      <Box
        sx={{
          maxWidth: { xs: "100%", sm: "85%", md: "75%" },
          minWidth: 0,
          p: 2,
          borderRadius: 3,
          borderTopRightRadius: mine ? "4px" : undefined,
          borderTopLeftRadius: mine ? undefined : "4px",
          border: "1px solid",
          borderColor: mine ? "transparent" : "divider",
          bgcolor: mine ? "zun.primaryTint" : "background.default",
          color: "text.primary"
        }}
      >
        <Stack direction="row" useFlexGap spacing={1} sx={{ alignItems: "center", flexWrap: "wrap", rowGap: 0.25, mb: 0.75 }}>
          <Icon aria-hidden sx={{ color: mine ? "primary.main" : "info.main" }} />
          <Typography component="span" sx={{ fontWeight: 650 }}>
            {mine ? "You" : "Student support"}
          </Typography>
          <Typography component="span" sx={{ color: "text.secondary" }}>
            <Box component="time" dateTime={isoOrUndefined(at)}>
              {formatDateTime(at)}
            </Box>
          </Typography>
        </Stack>
        <Typography sx={{ whiteSpace: "pre-line", overflowWrap: "anywhere" }}>{text}</Typography>
      </Box>
    </Box>
  );
}

export default function StudentTicketDetailPage() {
  const { id: ticketId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { currentTicket, loading, error, postingMessage } = useSelector((state) => state.support);

  // Shown once, right after "Ask for help" (state from navigate()).
  const [created] = useState(Boolean(location.state?.created));
  const createdRef = useRef(null);
  // The reply endpoint answers with the whole updated ticket; prefer it to
  // the slice's copy, which can't merge that answer.
  const [fresh, setFresh] = useState(null);
  const [reply, setReply] = useState("");
  const [replyError, setReplyError] = useState(null);
  const [replyNotice, setReplyNotice] = useState("");
  const replyRef = useRef(null);

  const load = useCallback(() => {
    setFresh(null);
    dispatch(fetchTicketDetail(ticketId));
  }, [dispatch, ticketId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    // Don't show the confirmation again on back/forward.
    if (location.state?.created) navigate(location.pathname, { replace: true, state: null });
  }, [location.state, location.pathname, navigate]);

  const ticket =
    fresh && String(fresh._id) === ticketId
      ? fresh
      : currentTicket && String(currentTicket._id) === ticketId
        ? currentTicket
        : null;

  // Move focus to the confirmation once the request is on screen.
  const announced = useRef(false);
  useEffect(() => {
    if (created && ticket && !announced.current) {
      announced.current = true;
      createdRef.current?.focus();
    }
  }, [created, ticket]);

  if (!ticket) {
    return (
      <Box>
        <PageHeader breadcrumbs={BREADCRUMBS} title="Support request" />
        {error && !loading ? (
          <>
            <ErrorState error={error} title="We couldn't open this request" onRetry={load} />
            <Button component={RouterLink} to="/student/support" variant="outlined">
              Back to Support
            </Button>
          </>
        ) : (
          <LoadingState variant="table" rows={3} label="Loading your request…" />
        )}
      </Box>
    );
  }

  const resolved = ticket.status === "RESOLVED";
  const messages = (Array.isArray(ticket.messages) ? ticket.messages : []).filter(
    (m) => m && (m.sender === "USER" || m.sender === "ADMIN") && typeof m.message === "string"
  );
  const lastUpdate = ticket.updatedAt || messages.at(-1)?.createdAt || ticket.createdAt;

  const send = async (e) => {
    e.preventDefault();
    setReplyError(null);
    setReplyNotice("");
    if (!reply.trim()) {
      setReplyError("MESSAGE_REQUIRED");
      replyRef.current?.focus();
      return;
    }
    const result = await dispatch(postTicketMessage({ ticketId, message: reply.trim() }));
    if (postTicketMessage.fulfilled.match(result)) {
      const updated = result.payload?.newMessage?.ticket;
      if (updated?._id) setFresh(updated);
      else dispatch(fetchTicketDetail(ticketId));
      setReply("");
      setReplyNotice("Reply sent. We'll answer within 48 hours.");
    } else {
      setReplyError(result.payload || result.error || "INTERNAL_ERROR");
    }
  };

  return (
    <Box sx={{ maxWidth: 900 }}>
      <PageHeader
        breadcrumbs={BREADCRUMBS}
        title={`${ticketCategoryLabel(ticket.category)} request`}
        actions={<TicketStatusChip status={ticket.status} size="medium" />}
      />

      {created && (
        <Alert severity="success" role="status" tabIndex={-1} ref={createdRef} sx={{ mb: 2 }}>
          Request sent. We reply within 48 hours.
        </Alert>
      )}

      <Stack spacing={2}>
        <Card>
          <CardContent>
            <DetailList
              columns={3}
              items={[
                ...(ticket.reference ? [{ label: "Reference", value: ticket.reference }] : []),
                { label: "Kind of help", value: <CategoryChip category={ticket.category} /> },
                { label: "Sent", value: formatDateTime(ticket.createdAt) },
                // MTO 10.2: every request is answered within 48 hours.
                ...(ticket.status !== "RESOLVED" && ticket.slaDueAt
                  ? [{ label: "We'll reply by", value: formatDateTime(ticket.slaDueAt) }]
                  : []),
                ...(ticket.resolvedAt ? [{ label: "Resolved", value: formatDateTime(ticket.resolvedAt) }] : []),
                { label: "Last update", value: formatDateTime(lastUpdate) }
              ]}
            />
          </CardContent>
        </Card>

        <Section id="ticket-conversation" title="Conversation">
          <Stack component="ol" spacing={1.5} sx={{ listStyle: "none", m: 0, p: 0 }} aria-label="Messages, oldest first">
            <Bubble mine text={ticket.description || "(No description)"} at={ticket.createdAt} />
            {messages.map((m, i) => (
              <Bubble key={m._id || i} mine={m.sender === "USER"} text={m.message} at={m.createdAt} />
            ))}
          </Stack>
          {messages.every((m) => m.sender !== "ADMIN") && !resolved && (
            <Typography sx={{ color: "text.secondary", mt: 2 }}>
              No reply yet. Student support answers every request within 48 hours.
            </Typography>
          )}
        </Section>

        <Section id="ticket-reply" title="Add a reply">
          {resolved && (
            <Alert severity="info" sx={{ mb: 2 }}>
              This request is resolved, so it can't take new replies. Still need help?{" "}
              <Link component={RouterLink} to="/student/support/new" sx={{ color: "inherit", fontWeight: 600 }}>
                Send a new request
              </Link>
              .
            </Alert>
          )}
          <Stack component="form" spacing={2} onSubmit={send} noValidate>
            {replyError && (
              <Alert severity="error" role="alert">
                {errorMessage(replyError)}
              </Alert>
            )}
            <TextField
              id="ticket-reply-text"
              inputRef={replyRef}
              label="Your reply"
              multiline
              minRows={4}
              fullWidth
              disabled={resolved}
              value={reply}
              onChange={(e) => {
                setReply(e.target.value);
                if (replyNotice) setReplyNotice("");
              }}
              slotProps={{ htmlInput: { maxLength: REPLY_MAX } }}
              helperText={
                resolved
                  ? "Replies are closed because this request is resolved."
                  : `${reply.length} of ${REPLY_MAX} characters`
              }
            />
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ alignItems: { xs: "stretch", sm: "center" } }}>
              <Button
                type="submit"
                variant="contained"
                disabled={resolved || postingMessage}
                startIcon={postingMessage ? <CircularProgress size={18} color="inherit" aria-hidden /> : <SendOutlinedIcon />}
              >
                {postingMessage ? "Sending…" : "Send reply"}
              </Button>
              <Typography role="status" sx={{ color: "text.secondary" }}>
                {replyNotice}
              </Typography>
            </Stack>
          </Stack>
        </Section>
      </Stack>
    </Box>
  );
}
